import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "@web/lib/cn";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name for the dialog. */
  label: string;
  children: ReactNode;
  className?: string;
}

/**
 * Native <dialog> in the top layer: focus is contained, Escape closes, the page behind is inert.
 * Clicking the backdrop closes too.
 */
export function Modal({ open, onClose, label, children, className }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label={label}
      className={cn("mm-dialog", className)}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {open ? children : null}
    </dialog>
  );
}

export function isModalOpen(): boolean {
  return document.querySelector("dialog[open]") !== null;
}
